/**
 * Skills page for ros-app.
 *
 * Ported from packages/web/src/components/skills/ — same UI and feature set,
 * adapted to ros-app's auth (useRosShell for apiBaseUrl + token) and design
 * tokens. JSZip skipped — downloads as .md only.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Switch,
  CircularProgress,
  InputAdornment,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
  Search as SearchIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  AutoStories as SkillIcon,
  AttachFile as AttachIcon,
  UploadFileOutlined as UploadIcon,
  Close as CloseIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  FileDownloadOutlined as DownloadIcon,
} from '@mui/icons-material'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { usePostHog } from '@reachinsight-app/shared/src'
import { useRosShell } from '../RosShellContext'
import skillsService, {
  buildSkillMarkdown,
  SkillRequestError,
} from '../services/SkillsService'
import type { SkillSummary, SkillDetail, SkillFormData } from '../services/SkillsService'
import { ds } from '../components/agent-builder/theme'
import { ExtensionCard } from '../components/ExtensionCard'
import { SERIF_HEADING } from '../theme'

// ── Utilities ──────────────────────────────────────────────────────────────

function slugify(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

function prettySkillName(name: string): string {
  return name.replace(/-/g, ' ')
}

// ── Constants ──────────────────────────────────────────────────────────────

type SortKey = 'new' | 'enabled' | 'recent'
const SORT_LABELS: Record<SortKey, string> = { new: 'Newest', enabled: 'Enabled first', recent: 'Recently used' }
const EMPTY_FORM: SkillFormData = { name: '', description: '', instructions: '', references: [] }
const MAX_REF_BYTES = 25 * 1024 * 1024

// ── Markdown styles ────────────────────────────────────────────────────────

const sxMarkdown = {
  fontSize: '0.875rem',
  lineHeight: 1.7,
  color: 'text.primary',
  '& h1, & h2, & h3, & h4': { fontFamily: ds.fontSerif, color: 'text.primary', mt: 2, mb: 1, fontWeight: 600 },
  '& h1': { fontSize: '1.15rem' },
  '& h2': { fontSize: '1.05rem' },
  '& h3, & h4': { fontSize: '0.95rem' },
  '& p': { my: 1 },
  '& ul, & ol': { pl: 3, my: 1 },
  '& li': { mb: 0.5 },
  '& code': { fontFamily: 'monospace', fontSize: '0.8125rem', bgcolor: 'action.hover', px: 0.5, borderRadius: ds.radiusSm },
  '& pre': { bgcolor: 'action.hover', p: 1.5, borderRadius: ds.radiusSm, overflow: 'auto' },
  '& pre code': { bgcolor: 'transparent', p: 0 },
  '& a': { color: 'primary.main' },
  '& > :first-of-type': { mt: 0 },
} as const

// Theme-aware card — same shape as the shared `sxCard` but reads background and border
// from the MUI palette so it flips correctly in dark mode. Local to this page (the shared
// `sxCard` still hardcodes the light palette and is used elsewhere).
const sxCardThemed = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: ds.radiusLg,
  p: 2.5,
} as const

// ── FilterPill ─────────────────────────────────────────────────────────────

// Master switch for the sort filter pills. Off for now (hidden from the UI) while the sort logic
// stays intact; set to true to re-expose the pills.
const SHOW_SORT_PILLS = false

function FilterPill({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <Box onClick={onClick} sx={{
      px: 1.5, py: 0.5, borderRadius: 999, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
      border: '1px solid', userSelect: 'none',
      borderColor: active ? 'primary.main' : 'divider',
      color: active ? 'primary.main' : 'text.secondary',
      bgcolor: active ? 'rgba(0,143,168,0.08)' : 'transparent',
      '&:hover': { bgcolor: active ? 'rgba(0,143,168,0.12)' : 'action.hover' },
    }}>
      {label}
    </Box>
  )
}

// ── SkillDetailView ────────────────────────────────────────────────────────

interface DetailProps {
  skill: SkillSummary
  onBack: () => void
  onEdit: () => void
  onToggle: () => void
  onDelete: () => void
  onDuplicate: (detail: SkillDetail) => void
  onError: (message: string) => void
}

const SkillDetailView: React.FC<DetailProps> = ({
  skill, onBack, onEdit, onToggle, onDelete, onDuplicate, onError,
}) => {
  const [tab, setTab] = useState<'instructions' | 'references'>('instructions')
  const [detail, setDetail] = useState<SkillDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    let active = true
    setLoading(true)
    skillsService.getSkill(skill.id)
      .then(d => { if (active) setDetail(d) })
      .catch(err => { if (active) onErrorRef.current(err instanceof Error ? err.message : 'Failed to load skill') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [skill.id])

  const references = detail?.references ?? []

  const handleDownload = async () => {
    if (!detail) return
    setDownloading(true)
    try {
      const slug = slugify(detail.name)
      const md = buildSkillMarkdown({
        name: slug,
        description: detail.description,
        instructions: detail.instructions,
        references: detail.references,
      })
      const blob = new Blob([md], { type: 'text/markdown' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${slug}.skill`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setDownloading(false)
    }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? [])
    e.target.value = ''
    const tooBig = picked.filter(f => f.size > MAX_REF_BYTES)
    if (tooBig.length > 0) onError(`${tooBig.map(f => f.name).join(', ')} exceed the 25 MB limit`)
    const ok = picked.filter(f => f.size <= MAX_REF_BYTES)
    if (ok.length === 0) return
    setUploading(true)
    try {
      for (const file of ok) {
        const { fileName } = await skillsService.uploadReference(skill.id, file)
        setDetail(d => d && !d.references.includes(fileName) ? { ...d, references: [...d.references, fileName] } : d)
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to upload reference')
    } finally {
      setUploading(false)
    }
  }

  const handleDeleteReference = async (fileName: string) => {
    try {
      await skillsService.deleteReference(skill.id, fileName)
      setDetail(d => d ? { ...d, references: d.references.filter(r => r !== fileName) } : d)
    } catch (err) {
      onError(err instanceof Error ? err.message : `Failed to delete ${fileName}`)
    }
  }

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto' }}>
      <Button startIcon={<BackIcon />} onClick={onBack} sx={{ ml: -1, mb: 1.5 }}>
        All skills
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: ds.radiusSm, bgcolor: 'rgba(0,143,168,0.08)', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <SkillIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...SERIF_HEADING, fontSize: '1.5rem', fontFamily: 'monospace' }}>
            {prettySkillName(skill.name)}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 0.75 }}>
            Created {new Date(skill.createdAt).toLocaleDateString()} · Updated {new Date(skill.updatedAt).toLocaleDateString()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Tooltip title={skill.enabled ? 'Enabled — Ros can load this skill' : 'Disabled'}>
            <Switch color="primary" checked={skill.enabled} onChange={onToggle} />
          </Tooltip>
          <Button variant="outlined" startIcon={<DuplicateIcon />} onClick={() => detail && onDuplicate(detail)} disabled={!detail}>
            Duplicate
          </Button>
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownload}
            disabled={!detail || downloading}
          >
            Download
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
            Remove
          </Button>
        </Box>
      </Box>

      <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2.5 }}>
        {skill.description}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        sx={{
          minHeight: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 40 },
        }}
      >
        <Tab value="instructions" label="Instructions" />
        <Tab value="references" label={`References${references.length ? ` (${references.length})` : ''}`} />
      </Tabs>

      <Box sx={{ ...sxCardThemed, borderTop: 'none', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} color="primary" />
          </Box>
        ) : tab === 'instructions' ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                Ros follows these instructions whenever this skill is applied.
              </Typography>
              <Button variant="outlined" startIcon={<EditIcon />} onClick={onEdit}>Edit</Button>
            </Box>
            <Box sx={sxMarkdown}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ hr: () => <Box sx={{ my: 1.5 }} /> }}>
                {detail?.instructions || '_No instructions yet._'}
              </ReactMarkdown>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                Files Ros can consult when this skill is applied.
              </Typography>
              <input ref={fileInputRef} type="file" accept=".md" multiple hidden onChange={handleUpload} />
              <Button
                variant="outlined"
                color="primary"
                startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                Upload reference
              </Button>
            </Box>
            {references.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                <AttachIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                <Typography sx={{ fontSize: '0.8125rem' }}>No references yet — upload a file Ros can consult.</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {references.map((ref, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderBottom: i < references.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <AttachIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', flex: 1 }}>{ref}</Typography>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteReference(ref)} aria-label={`Delete ${ref}`} sx={{ color: 'text.primary', '&:hover': { color: 'error.main' } }}>
                        <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

// ── SkillsPage ─────────────────────────────────────────────────────────────

export function SkillsPage() {
  const shell = useRosShell()
  const theme = useTheme()
  const { trackEvent } = usePostHog()

  // Configure the service in the render body so it's always up to date.
  skillsService.configure(shell.apiBaseUrl, shell.token)

  const [skills, setSkills] = useState<SkillSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('new')
  const [viewId, setViewId] = useState<string | null>(null)

  const [editorOpen, setEditorOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<SkillFormData>(EMPTY_FORM)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formErrors, setFormErrors] = useState<string[]>([])
  const importInputRef = useRef<HTMLInputElement>(null)
  const [importing, setImporting] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<SkillSummary | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' })
  const notify = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity })

  const loadSkills = useCallback(async () => {
    setLoading(true)
    try {
      setSkills(await skillsService.listSkills())
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to load skills', 'error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadSkills() }, [loadSkills])

  const visibleSkills = useMemo(() => {
    const q = search.trim().toLowerCase()
    const filtered = q
      ? skills.filter(s => prettySkillName(s.name).toLowerCase().includes(q) || s.description.toLowerCase().includes(q))
      : skills
    const sorted = [...filtered]
    if (sortKey === 'enabled') sorted.sort((a, b) => Number(b.enabled) - Number(a.enabled) || b.updatedAt - a.updatedAt)
    else if (sortKey === 'recent') sorted.sort((a, b) => (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0) || b.updatedAt - a.updatedAt)
    else sorted.sort((a, b) => b.createdAt - a.createdAt)
    return sorted
  }, [skills, search, sortKey])

  const openCreate = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setPendingFiles([])
    setFormErrors([])
    setEditorOpen(true)
  }

  const openEdit = async (skill: SkillSummary) => {
    setEditingId(skill.id)
    setForm({ name: prettySkillName(skill.name), description: skill.description, instructions: '' })
    setFormErrors([])
    setEditorOpen(true)
    setLoadingDetail(true)
    try {
      const detail = await skillsService.getSkill(skill.id)
      setForm({ name: prettySkillName(detail.name), description: detail.description, instructions: detail.instructions })
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to load skill', 'error')
      setEditorOpen(false)
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleImportPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > MAX_REF_BYTES) { notify(`${file.name} exceeds the 25 MB limit`, 'error'); return }
    setImporting(true)
    try {
      const created = await skillsService.importSkill(file)
      trackEvent('ros_skill_created', {skill_name: created.name, source: 'import'})
      notify(`Imported "${prettySkillName(created.name)}"`)
      await loadSkills()
    } catch (err) {
      const message = err instanceof SkillRequestError && err.details.length > 0
        ? err.details.join('; ')
        : err instanceof Error ? err.message : 'Import failed'
      notify(message, 'error')
    } finally {
      setImporting(false)
    }
  }

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? [])
    e.target.value = ''
    const tooBig = picked.filter(f => f.size > MAX_REF_BYTES)
    if (tooBig.length > 0) notify(`${tooBig.map(f => f.name).join(', ')} exceed the 25 MB limit`, 'error')
    const ok = picked.filter(f => f.size <= MAX_REF_BYTES)
    setPendingFiles(prev => { const taken = new Set(prev.map(f => f.name)); return [...prev, ...ok.filter(f => !taken.has(f.name))] })
  }

  const removePending = (index: number) => setPendingFiles(prev => prev.filter((_, i) => i !== index))

  const handleSave = async () => {
    setSaving(true)
    setFormErrors([])
    try {
      const name = slugify(form.name)
      if (editingId) {
        await skillsService.updateSkill(editingId, { name, description: form.description.trim(), instructions: form.instructions.trim() })
        notify('Skill updated')
      } else {
        const created = await skillsService.createSkill({ ...form, name })
        for (const file of pendingFiles) await skillsService.uploadReference(created.id, file)
        trackEvent('ros_skill_created', {skill_name: created.name, source: 'create'})
        notify('Skill created')
      }
      setEditorOpen(false)
      await loadSkills()
    } catch (err) {
      if (err instanceof SkillRequestError && err.details.length > 0) setFormErrors(err.details)
      else notify(err instanceof Error ? err.message : 'Failed to save skill', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (skill: SkillSummary) => {
    const next = !skill.enabled
    setSkills(prev => prev.map(s => s.id === skill.id ? { ...s, enabled: next } : s))
    try {
      await skillsService.updateSkill(skill.id, { enabled: next })
    } catch (err) {
      setSkills(prev => prev.map(s => s.id === skill.id ? { ...s, enabled: skill.enabled } : s))
      notify(err instanceof Error ? err.message : 'Failed to update skill', 'error')
    }
  }

  const handleDuplicate = async (detail: SkillDetail) => {
    try {
      const name = slugify(`${detail.name}-copy`)
      const created = await skillsService.duplicateSkill(detail.id, name)
      trackEvent('ros_skill_created', {skill_name: created.name, source: 'duplicate'})
      notify(`Duplicated as "${prettySkillName(created.name)}"`)
      await loadSkills()
      setViewId(created.id)
    } catch (err) {
      notify(err instanceof SkillRequestError && err.details.length > 0 ? err.details.join('; ') : err instanceof Error ? err.message : 'Failed to duplicate skill', 'error')
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await skillsService.deleteSkill(deleteTarget.id)
      notify(`Deleted "${prettySkillName(deleteTarget.name)}"`)
      if (viewId === deleteTarget.id) setViewId(null)
      setDeleteTarget(null)
      await loadSkills()
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to delete skill', 'error')
    } finally {
      setDeleting(false)
    }
  }

  const nameSlug = slugify(form.name)
  const duplicateName = !!nameSlug && skills.some(s => s.id !== editingId && s.name.toLowerCase() === nameSlug)
  const canSave = nameSlug && form.description.trim() && form.instructions.trim() && !loadingDetail && !duplicateName
  const viewSkill = viewId ? (skills.find(s => s.id === viewId) ?? null) : null

  return (
    <Box>
      <Box>
        {viewSkill ? (
          <SkillDetailView
            key={`${viewSkill.id}-${viewSkill.updatedAt}`}
            skill={viewSkill}
            onBack={() => setViewId(null)}
            onEdit={() => openEdit(viewSkill)}
            onToggle={() => handleToggle(viewSkill)}
            onDelete={() => setDeleteTarget(viewSkill)}
            onDuplicate={handleDuplicate}
            onError={msg => notify(msg, 'error')}
          />
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search skills..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
                sx={{ maxWidth: 320, '& .MuiOutlinedInput-root': { borderRadius: ds.radiusSm, fontSize: '0.8125rem' } }}
              />
              {/* Sort filter pills hidden for now. Sort logic (sortKey/setSortKey) is retained;
                  flip SHOW_SORT_PILLS to true to bring the pills back. */}
              {SHOW_SORT_PILLS && (Object.keys(SORT_LABELS) as SortKey[]).map(k => (
                <FilterPill key={k} active={sortKey === k} label={SORT_LABELS[k]} onClick={() => setSortKey(k)} />
              ))}
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                onClick={() => importInputRef.current?.click()}
                disabled={importing}
              >
                {importing ? 'Importing…' : 'Import'}
              </Button>
              <input ref={importInputRef} type="file" accept=".skill,.md,.zip" hidden onChange={handleImportPick} />
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                Create skill
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress color="primary" />
              </Box>
            ) : visibleSkills.length === 0 ? (
              <Box sx={{ ...sxCardThemed, textAlign: 'center', py: 6, color: 'text.secondary' }}>
                <SkillIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                  {search ? 'No skills match your search' : 'No skills yet'}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem' }}>
                  {search ? 'Try a different term.' : 'Create a skill to give Ros a reusable playbook.'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.75 }}>
                {visibleSkills.map(skill => (
                  <ExtensionCard
                    key={skill.id}
                    icon={AutoAwesomeIcon}
                    iconColor={theme.palette.primary.main}
                    iconBg={alpha(theme.palette.primary.main, 0.12)}
                    title={prettySkillName(skill.name)}
                    description={skill.description}
                    chips={skill.enabled ? undefined : ['Disabled']}
                    meta={`Updated ${new Date(skill.updatedAt).toLocaleDateString()}`}
                    toggle={{ checked: skill.enabled, onChange: () => handleToggle(skill) }}
                    onCardClick={() => setViewId(skill.id)}
                    menuItems={[
                      { label: 'Edit', icon: EditIcon, onClick: () => openEdit(skill) },
                      { label: 'Delete', icon: DeleteIcon, onClick: () => setDeleteTarget(skill), danger: true },
                    ]}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Create / Edit modal */}
      <Dialog open={editorOpen} onClose={() => !saving && setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ ...SERIF_HEADING, fontSize: '1.1rem' }}>
          {editingId ? 'Edit skill' : 'Create a skill'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '12px !important' }}>
          {!editingId && (
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              Tip: you can also just ask Ros — &ldquo;create a skill for drafting surveys&rdquo; — and she&rsquo;ll write the instructions for you.
            </Typography>
          )}
          {formErrors.length > 0 && (
            <Alert severity="error" sx={{ borderRadius: ds.radiusSm }}>
              <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                {formErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </Alert>
          )}
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Name</Typography>
            <TextField
              placeholder="e.g. Concept test analysis"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              fullWidth size="small"
              error={duplicateName}
              helperText={duplicateName ? `A skill named "${prettySkillName(nameSlug)}" already exists — choose a different name.` : undefined}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Description</Typography>
            <TextField
              placeholder="When should Ros reach for this skill?"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              fullWidth size="small" multiline rows={2}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Instructions</Typography>
            {loadingDetail ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} color="primary" />
              </Box>
            ) : (
              <TextField
                value={form.instructions}
                onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))}
                fullWidth multiline rows={14}
                placeholder={'## When to use\n…\n\n## Approach\n- …'}
                sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace', fontSize: '0.8125rem', lineHeight: 1.7 } }}
              />
            )}
          </Box>
          {!editingId && (
            <Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                References <Box component="span" sx={{ fontWeight: 400, color: 'text.disabled' }}>(optional)</Box>
              </Typography>
              {pendingFiles.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                  {pendingFiles.map((file, i) => (
                    <Box key={`${file.name}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', flex: 1 }}>{file.name}</Typography>
                      <IconButton size="small" onClick={() => removePending(i)} aria-label={`Remove ${file.name}`}>
                        <CloseIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              <input ref={fileInputRef} type="file" accept=".md" multiple hidden onChange={handleFilePick} />
              <Button
                variant="outlined"
                color="primary"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload reference
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditorOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !canSave}>
            {saving ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
            {editingId ? 'Save changes' : 'Create skill'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ ...SERIF_HEADING, fontSize: '1.05rem' }}>Delete skill?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
            <strong>{deleteTarget ? prettySkillName(deleteTarget.name) : ''}</strong>{' '}
            will be permanently removed. This can&apos;t be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
